// src/pages/funnels/FunnelsList.jsx
// Shared list + create screen for both surfaces backed by the `funnels` table:
//
//   /funnels        kind="funnel"  — multi-step opt-in funnels
//   /landing-pages  kind="page"    — standalone single pages
//
// A page is a funnel with exactly one step (see the kind column in
// supabase/2026-07-29-pages-and-standalone-tests.sql), so the two screens are
// the same query, editor and publisher with different copy. Keeping them in
// one component is what stops them drifting apart.
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { authFetch } from '../../lib/authFetch';
import {
  Card, CardContent, Button, Input, Modal, ModalFooter, Badge,
} from '../../components/ui';
import { formatNumber, cn } from '../../lib/utils';
import {
  Plus, Trash2, Copy, ExternalLink, Loader2, Filter, Eye, Users, Layers, FileText,
} from 'lucide-react';
import toast from 'react-hot-toast';
import TemplateGallery from './TemplateGallery';

const PAGES_DOMAIN = 'pages.revhapi.com';

const COPY = {
  funnel: {
    icon: Filter,
    title: 'Funnels',
    subtitle: 'Multi-step opt-in funnels with drag-and-drop pages, lead capture and email automation.',
    newLabel: 'New Funnel',
    emptyTitle: 'No funnels yet',
    emptyBody: 'Create your first opt-in funnel — start from a proven industry template or build from scratch in the drag-and-drop editor.',
    emptyCta: 'Create your first funnel',
    modalTitle: 'New Funnel',
    nameLabel: 'Funnel name',
    namePlaceholder: 'e.g. Free Guide Opt-in',
    blankLabel: 'Blank funnel',
    blankHint: 'Starts with an opt-in page and a thank-you page — customise both in the editor.',
    createLabel: 'Create Funnel',
    deleteWarning: 'Its pages, leads and stats will be permanently removed.',
    limitNoun: 'funnel',
    showSteps: true,
  },
  page: {
    icon: FileText,
    title: 'Landing Pages',
    subtitle: 'Standalone single pages built in the same drag-and-drop editor as funnels.',
    newLabel: 'New Page',
    emptyTitle: 'No landing pages yet',
    emptyBody: 'Build a standalone page — a lead magnet, a webinar registration, a link-in-bio page — without wiring up a whole funnel.',
    emptyCta: 'Create your first page',
    modalTitle: 'New Landing Page',
    nameLabel: 'Page name',
    namePlaceholder: 'e.g. Free Guide Download',
    blankLabel: 'Blank page',
    blankHint: 'Starts with a single opt-in page you can shape however you like.',
    createLabel: 'Create Page',
    deleteWarning: 'Its content, leads and stats will be permanently removed.',
    limitNoun: 'page',
    showSteps: false,
  },
};

function funnelUrl(funnel) {
  if (funnel.custom_domain_id && funnel.custom_domains?.domain) {
    return `https://${funnel.custom_domains.domain}/${funnel.custom_slug || ''}`;
  }
  return `https://${PAGES_DOMAIN}/${funnel.slug}`;
}

function cvr(funnel) {
  if (!funnel.total_views) return '—';
  return `${(((funnel.total_leads || 0) / funnel.total_views) * 100).toFixed(1)}%`;
}

export default function FunnelsList({ kind = 'funnel' }) {
  const navigate = useNavigate();
  const { tier, isAdmin } = useAuth();
  const copy = COPY[kind] || COPY.funnel;

  const [funnels, setFunnels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createName, setCreateName] = useState('');
  // Pages default to blank: most single pages are bespoke, and the templates
  // are authored as multi-step funnels.
  const [createMode, setCreateMode] = useState(kind === 'page' ? 'blank' : 'template');
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const loadFunnels = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`/api/funnels?resource=funnels&kind=${kind}`);
      const data = await res.json();
      if (data.success) setFunnels(data.funnels || []);
      else toast.error(data.error || `Failed to load ${copy.limitNoun}s`);
    } catch (e) {
      console.error('Funnels load error:', e);
      toast.error(`Failed to load ${copy.limitNoun}s`);
    } finally {
      setLoading(false);
    }
  }, [kind, copy.limitNoun]);

  useEffect(() => { loadFunnels(); }, [loadFunnels]);

  // The tier cap counts funnels and pages together — they're rows in the same
  // table and cost the same to serve.
  const atStarterLimit = tier === 'starter' && !isAdmin && funnels.length >= 1;

  const handleCreate = async () => {
    const name = createName.trim() || selectedTemplate?.name || '';
    if (!name) {
      toast.error(`Give your ${copy.limitNoun} a name`);
      return;
    }
    setCreating(true);
    try {
      const res = await authFetch('/api/funnels?resource=funnels', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'create',
          name,
          kind,
          template_key: createMode === 'template' ? selectedTemplate?.template_key : undefined,
        }),
      });
      const data = await res.json();
      if (!data.success) {
        toast.error(data.error || `Failed to create ${copy.limitNoun}`);
        return;
      }

      // A page has exactly one step, so the detail screen would just be a
      // waypoint — drop straight into the editor instead.
      if (kind === 'page') {
        const detail = await authFetch(`/api/funnels?resource=funnels&funnelId=${data.funnel.id}`).then(r => r.json());
        const stepId = detail?.steps?.[0]?.id;
        if (stepId) {
          toast.success('Page created!');
          navigate(`/funnels/${data.funnel.id}/edit/${stepId}`);
          return;
        }
      }
      toast.success(`${copy.limitNoun === 'page' ? 'Page' : 'Funnel'} created!`);
      navigate(`/funnels/${data.funnel.id}`);
    } catch (e) {
      console.error('Create error:', e);
      toast.error(`Failed to create ${copy.limitNoun}`);
    } finally {
      setCreating(false);
    }
  };

  const handleDuplicate = async (funnel) => {
    try {
      const res = await authFetch('/api/funnels?resource=funnels', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'duplicate', funnelId: funnel.id }),
      });
      const data = await res.json();
      if (!data.success) return toast.error(data.error || 'Failed to duplicate');
      toast.success('Duplicated');
      loadFunnels();
    } catch {
      toast.error('Failed to duplicate');
    }
  };

  const handleDelete = async (funnel) => {
    if (!window.confirm(`Delete "${funnel.name}"? ${copy.deleteWarning}`)) return;
    try {
      const res = await authFetch('/api/funnels?resource=funnels', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'delete', funnelId: funnel.id }),
      });
      const data = await res.json();
      if (!data.success) return toast.error(data.error || 'Failed to delete');
      toast.success('Deleted');
      setFunnels(prev => prev.filter(f => f.id !== funnel.id));
    } catch {
      toast.error('Failed to delete');
    }
  };

  const openCreate = () => {
    if (atStarterLimit) {
      toast.error(`Starter plan includes 1 ${copy.limitNoun}. Upgrade to Pro for unlimited.`);
      return;
    }
    setCreateName('');
    setCreateMode(kind === 'page' ? 'blank' : 'template');
    setSelectedTemplate(null);
    setShowCreate(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  const EmptyIcon = copy.icon;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold">{copy.title}</h1>
          <p className="text-sm text-muted-foreground">{copy.subtitle}</p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="h-4 w-4 mr-2" /> {copy.newLabel}
        </Button>
      </div>

      {atStarterLimit && (
        <div className="rounded-lg border border-amber-300/50 bg-amber-50 dark:bg-amber-950/30 px-4 py-3 text-sm">
          Starter plan includes 1 {copy.limitNoun} — you're using {funnels.length}/1.{' '}
          <Link to="/subscribe" className="font-medium text-primary hover:underline">Upgrade to Pro</Link>{' '}
          for unlimited.
        </div>
      )}

      {/* Pages built with the old section builder aren't listed here — they're
          a different content format. They keep serving and stay editable, so
          point at them rather than leaving people to wonder where they went. */}
      {kind === 'page' && (
        <p className="text-xs text-muted-foreground">
          Built pages with the old editor?{' '}
          <Link to="/landing-pages/legacy" className="text-primary hover:underline">
            Find them here
          </Link>{' '}
          — they'll keep working.
        </p>
      )}

      {funnels.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <EmptyIcon className="h-10 w-10 text-muted-foreground/40 mb-3" />
            <h3 className="font-semibold mb-1">{copy.emptyTitle}</h3>
            <p className="text-sm text-muted-foreground mb-4 max-w-sm">{copy.emptyBody}</p>
            <Button onClick={openCreate}>
              <Plus className="h-4 w-4 mr-2" /> {copy.emptyCta}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {funnels.map(funnel => (
            <Card key={funnel.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center gap-4 flex-wrap">
                  <Link to={`/funnels/${funnel.id}`} className="flex-1 min-w-[200px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold hover:text-primary transition-colors">{funnel.name}</span>
                      <Badge
                        className={cn(
                          'text-[10px] px-1.5 py-0.5',
                          funnel.status === 'published' ? 'bg-green-500' : 'bg-muted-foreground/60'
                        )}
                      >
                        {funnel.status === 'published' ? 'Published' : 'Draft'}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 truncate">
                      {funnelUrl(funnel).replace('https://', '')}
                    </p>
                  </Link>

                  <div className="flex items-center gap-5 text-sm">
                    {copy.showSteps && (
                      <span className="flex items-center gap-1.5 text-muted-foreground" title="Steps">
                        <Layers className="h-3.5 w-3.5" /> {funnel.steps_count}
                      </span>
                    )}
                    <span className="flex items-center gap-1.5 text-muted-foreground" title="Views">
                      <Eye className="h-3.5 w-3.5" /> {formatNumber(funnel.total_views || 0)}
                    </span>
                    <span className="flex items-center gap-1.5 text-muted-foreground" title="Leads">
                      <Users className="h-3.5 w-3.5" /> {formatNumber(funnel.total_leads || 0)}
                    </span>
                    <span className="text-muted-foreground w-14 text-right" title="Conversion rate">
                      {cvr(funnel)}
                    </span>
                  </div>

                  <div className="flex items-center gap-1">
                    {funnel.status === 'published' && (
                      <a
                        href={funnelUrl(funnel)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 hover:bg-muted rounded-lg text-muted-foreground"
                        title="View live"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    )}
                    <button
                      onClick={() => handleDuplicate(funnel)}
                      className="p-2 hover:bg-muted rounded-lg text-muted-foreground"
                      title="Duplicate"
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(funnel)}
                      className="p-2 hover:bg-muted rounded-lg text-muted-foreground hover:text-destructive"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Modal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        title={copy.modalTitle}
        size={kind === 'page' ? 'lg' : '2xl'}
      >
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-1.5 block">{copy.nameLabel}</label>
            <Input
              value={createName}
              onChange={e => setCreateName(e.target.value)}
              placeholder={copy.namePlaceholder}
              autoFocus
            />
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setCreateMode('template')}
              className={cn(
                'flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition-colors',
                createMode === 'template' ? 'border-primary bg-primary/5 text-primary' : 'border-border text-muted-foreground hover:bg-muted'
              )}
            >
              Start from template
            </button>
            <button
              type="button"
              onClick={() => { setCreateMode('blank'); setSelectedTemplate(null); }}
              className={cn(
                'flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition-colors',
                createMode === 'blank' ? 'border-primary bg-primary/5 text-primary' : 'border-border text-muted-foreground hover:bg-muted'
              )}
            >
              {copy.blankLabel}
            </button>
          </div>

          {createMode === 'template' ? (
            <>
              {kind === 'page' && (
                <p className="text-xs text-muted-foreground">
                  Templates are built as multi-step funnels — only the first page is used here.
                </p>
              )}
              <TemplateGallery
                selectedKey={selectedTemplate?.template_key}
                onSelect={t => {
                  setSelectedTemplate(t);
                  if (!createName.trim()) setCreateName(t.name);
                }}
              />
            </>
          ) : (
            <p className="text-sm text-muted-foreground">{copy.blankHint}</p>
          )}
        </div>

        <ModalFooter>
          <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
          <Button
            onClick={handleCreate}
            disabled={creating || (createMode === 'template' && !selectedTemplate)}
          >
            {creating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {copy.createLabel}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
